import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Paper, Stack, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { CLP } from '../utils/finance';
import { auth } from '../firebase';
import {
  deleteHonorarioLiquidacion,
  getHonorarioAjustes,
  getHonorarioContratos,
  getHonorarioLiquidaciones,
  saveHonorarioLiquidacion,
  sendLiquidacionToMedico,
  approvePagoHonorarios,
  markLiquidacionPagada,
  updateHonorarioLiquidacion,
  recalcularResumenLiquidacion,
  exportLiquidacionCSV,
} from '../services/honorarioService';

const ESTADO_LABEL = {
  borrador: 'Borrador',
  aprobada_por_administrador: 'Aprobada por administrador',
  aprobada_por_medico: 'Aprobada por médico',
  pago_aprobado: 'Pago aprobado',
  pagada: 'Pagada',
};

const ESTADO_COLOR = {
  borrador: 'default',
  aprobada_por_administrador: 'info',
  aprobada_por_medico: 'primary',
  pago_aprobado: 'warning',
  pagada: 'success',
};

export default function HonorariosLiquidacionesTab({ professionals }) {
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [periodoTab, setPeriodoTab] = useState(0); // 0 activas, 1 historial
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const periodo = `${anio}-${String(mes + 1).padStart(2, '0')}`;

  const load = async () => {
    setLoading(true);
    try {
      const data = await getHonorarioLiquidaciones('', periodo);
      setLiquidaciones(data);
    } catch (e) {
      console.error('Error cargando liquidaciones:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [periodo]);

  const fechasPeriodo = () => {
    const desde = `${anio}-${String(mes + 1).padStart(2, '0')}-01`;
    const hasta = new Date(anio, mes + 1, 0).toISOString().split('T')[0];
    return { desde, hasta };
  };

  const vigenteEnPeriodo = (c, desde, hasta) => {
    if (c.active === false) return false;
    if (c.validFrom && c.validFrom > hasta) return false;
    if (c.validTo && c.validTo < desde) return false;
    return true;
  };

  const handleGenerarTodas = async () => {
    setGenerating(true);
    try {
      const { desde, hasta } = fechasPeriodo();
      const existentes = await getHonorarioLiquidaciones('', periodo);
      const existentesSet = new Set(existentes.map((l) => l.professionalId));
      const contratos = await getHonorarioContratos('', 'vigente');
      const pidsConContrato = new Set(
        contratos.filter((c) => vigenteEnPeriodo(c, desde, hasta)).map((c) => c.professionalId)
      );
      const profesionalesFaltantes = professionals.filter(
        (p) => pidsConContrato.has(p.id) && !existentesSet.has(p.id)
      );

      for (const p of profesionalesFaltantes) {
        try {
          const ajustes = await getHonorarioAjustes(p.id, desde, hasta);
          await saveHonorarioLiquidacion({
            professionalId: p.id,
            periodo,
            fechaDesde: desde,
            fechaHasta: hasta,
            createdBy: auth.currentUser?.uid || '',
          }, ajustes);
        } catch (err) {
          console.error(`Error generando liquidación para ${p.id}:`, err);
        }
      }
      await load();
    } catch (e) {
      console.error('Error generando liquidaciones:', e);
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenDetail = (liq) => {
    setDetail(liq);
    setDetailItems((liq.items || []).map((i) => ({ ...i })));
    setDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDialogOpen(false);
    setDetail(null);
    setDetailItems([]);
  };

  const handleDeleteItem = (id) => {
    setDetailItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleEditHonorario = (id, value) => {
    const monto = Number(value) || 0;
    setDetailItems((prev) => prev.map((i) => (i.id === id ? { ...i, honorario: monto, honorarioCalculado: monto } : i)));
  };

  const handleSaveDetail = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await updateHonorarioLiquidacion(detail.id, { items: detailItems });
      handleCloseDetail();
      await load();
    } catch (e) {
      console.error('Error guardando detalle:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (id) => {
    try {
      await sendLiquidacionToMedico(id, auth.currentUser?.uid || '');
      await load();
    } catch (e) {
      console.error('Error enviando:', e);
    }
  };

  const handleAprobarPago = async (id) => {
    try {
      await approvePagoHonorarios(id, auth.currentUser?.uid || '');
      await load();
    } catch (e) {
      console.error('Error aprobando pago:', e);
    }
  };

  const handlePagar = async (id) => {
    try {
      await markLiquidacionPagada(id, auth.currentUser?.uid || '');
      await load();
    } catch (e) {
      console.error('Error pagando:', e);
    }
  };

  const handleEliminar = async (id) => {
    try {
      await deleteHonorarioLiquidacion(id);
      await load();
    } catch (e) {
      console.error('Error eliminando:', e);
    }
  };

  const handleExport = (liq) => {
    const csv = exportLiquidacionCSV(liq.items || []);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `liquidacion_${liq.professionalId}_${liq.periodo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const profName = (id) => {
    const p = professionals.find((x) => x.id === id);
    return p?.fullName || `${p?.nombre || ''} ${p?.apellidos || ''}`.trim() || id;
  };

  const resumenCalculado = useMemo(() => recalcularResumenLiquidacion(detailItems), [detailItems]);

  const activas = liquidaciones.filter((l) => l.estado !== 'pagada');
  const historial = liquidaciones.filter((l) => l.estado === 'pagada');
  const mostrar = periodoTab === 0 ? activas : historial;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Genera, revisa, aprueba y paga liquidaciones mensuales.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField select label="Mes" value={mes} onChange={(e) => setMes(Number(e.target.value))} sx={{ minWidth: 120 }}>
            {Array.from({ length: 12 }, (_, i) => (
              <MenuItem key={i} value={i}>{String(i + 1).padStart(2, '0')}</MenuItem>
            ))}
          </TextField>
          <TextField type="number" label="Año" value={anio} onChange={(e) => setAnio(Number(e.target.value))} sx={{ minWidth: 100 }} />
          <Button variant="contained" onClick={handleGenerarTodas} disabled={generating}>
            {generating ? 'Generando...' : 'Generar todas las del mes'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={periodoTab} onChange={(_, v) => setPeriodoTab(v)}>
          <Tab label="Activas" />
          <Tab label="Historial (pagadas)" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Período</TableCell>
                <TableCell>Profesional</TableCell>
                <TableCell>Actos</TableCell>
                <TableCell>Honorarios</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mostrar.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No hay liquidaciones en esta sección.
                  </TableCell>
                </TableRow>
              )}
              {mostrar.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.periodo}</TableCell>
                  <TableCell>{profName(l.professionalId)}</TableCell>
                  <TableCell>{l.resumen?.totalActos ?? 0}</TableCell>
                  <TableCell>{CLP(l.resumen?.totalHonorarios ?? 0)}</TableCell>
                  <TableCell>
                    <Chip label={ESTADO_LABEL[l.estado] || l.estado} color={ESTADO_COLOR[l.estado] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleOpenDetail(l)}>Revisar</Button>
                    {l.boletaHonorarios && (
                      <Button size="small" variant="outlined" onClick={() => window.open(l.boletaHonorarios.url, '_blank')}>
                        Ver boleta
                      </Button>
                    )}
                    {l.estado === 'borrador' && <Button size="small" onClick={() => handleSend(l.id)}>Enviar</Button>}
                    {l.estado === 'aprobada_por_medico' && <Button size="small" onClick={() => handleAprobarPago(l.id)}>Aprobar pago</Button>}
                    {l.estado === 'pago_aprobado' && <Button size="small" onClick={() => handlePagar(l.id)}>Marcar pagado</Button>}
                    <Button size="small" onClick={() => handleExport(l)}>CSV</Button>
                    <Button size="small" color="error" onClick={() => handleEliminar(l.id)}>Eliminar</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDetail} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Revisar liquidación — {profName(detail?.professionalId)}</DialogTitle>
        <DialogContent>
          {detail && (
            <Box>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Typography variant="body2">Actos: <strong>{resumenCalculado.totalActos}</strong></Typography>
                <Typography variant="body2">Cobrado: <strong>{CLP(resumenCalculado.totalCobrado)}</strong></Typography>
                <Typography variant="body2">Honorarios: <strong>{CLP(resumenCalculado.totalHonorarios)}</strong></Typography>
                {detail?.boletaHonorarios && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => window.open(detail.boletaHonorarios.url, '_blank')}
                  >
                    Ver boleta cargada
                  </Button>
                )}
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Paciente</TableCell>
                      <TableCell>Prestación</TableCell>
                      <TableCell>Origen</TableCell>
                      <TableCell>Monto Cobrado</TableCell>
                      <TableCell>Honorario</TableCell>
                      <TableCell>Eliminar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.fecha}</TableCell>
                        <TableCell>{item.paciente}</TableCell>
                        <TableCell>{item.prestacion || item.tipoPrestacionName}</TableCell>
                        <TableCell>{item.origen}</TableCell>
                        <TableCell>{CLP(item.montoCobrado)}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.honorario || 0}
                            onChange={(e) => handleEditHonorario(item.id, e.target.value)}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Cerrar</Button>
          <Button onClick={handleSaveDetail} variant="contained" disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
          {detail?.estado === 'borrador' && (
            <Button onClick={() => { handleSend(detail.id); handleCloseDetail(); }} variant="contained" color="primary">
              Enviar al médico
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
